import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Alert, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { sharedExpensesApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import CreateExpenseDialog from '../CreateExpenseDialog';
import LoadingButton from '../LoadingButton';
import { formatMoney } from '../../utils/money';

const formatAmount = (value) => formatMoney(Number(value));
const nameOf = (person) => person.name || person.email;

// What this expense means for the current user, or between the current user
// and one friend when `focusEmail` is given (friend page)
function YourPosition({ expense, focusEmail }) {
  if (!expense.involved) {
    return <Typography variant="body2" color="text.secondary">Not involved</Typography>;
  }

  if (focusEmail && !expense.group) {
    const friendSplit = expense.splits.find(s => s.email === focusEmail);
    const yourSplit = expense.splits.find(s => s.isYou);
    if (expense.paidBy.isYou && friendSplit) {
      return friendSplit.isSettled ? (
        <Typography variant="body2" color="text.secondary">{nameOf(friendSplit)} settled up</Typography>
      ) : (
        <Typography variant="body2" color="success.main">
          You lent {nameOf(friendSplit)} {formatAmount(friendSplit.shareAmount)}
        </Typography>
      );
    }
    if (expense.paidBy.email === focusEmail && yourSplit) {
      return yourSplit.isSettled ? (
        <Typography variant="body2" color="text.secondary">You settled up</Typography>
      ) : (
        <Typography variant="body2" color="error.main">
          You owe {nameOf(expense.paidBy)} {formatAmount(yourSplit.shareAmount)}
        </Typography>
      );
    }
  }

  if (expense.paidBy.isYou) {
    return expense.youLent > 0 ? (
      <Typography variant="body2" color="success.main">You lent {formatAmount(expense.youLent)}</Typography>
    ) : (
      <Typography variant="body2" color="text.secondary">Nothing left to collect</Typography>
    );
  }
  return expense.youOwe > 0 ? (
    <Typography variant="body2" color="error.main">You owe {formatAmount(expense.youOwe)}</Typography>
  ) : (
    <Typography variant="body2" color="text.secondary">Your share is settled</Typography>
  );
}

// Shared expenses as cards, with edit and delete for expenses you created
function ExpenseList({ expenses, focusEmail = null, showGroup = true, emptyText, onChanged }) {
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { showError, showSuccess } = useNotification();

  const handleUpdate = async (expenseData) => {
    await sharedExpensesApi.update(editTarget.id, expenseData);
    showSuccess('Expense updated');
    if (onChanged) await onChanged();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await sharedExpensesApi.delete(deleteTarget.id);
      showSuccess('Expense deleted');
      setDeleteTarget(null);
      if (onChanged) await onChanged();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  if (expenses.length === 0) {
    return <Alert severity="info">{emptyText || 'No expenses yet.'}</Alert>;
  }

  return (
    <>
      <Stack spacing={2}>
        {expenses.map((expense) => (
          <Card key={expense.id} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="subtitle1">{expense.description}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {expense.expenseDate} • Paid by {expense.paidBy.isYou ? 'you' : nameOf(expense.paidBy)}
                  </Typography>
                  {showGroup && expense.group && (
                    <Chip label={expense.group.name} size="small" variant="outlined" sx={{ mt: 1 }} />
                  )}
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">{formatAmount(expense.amount)}</Typography>
                    {expense.createdByMe && (
                      <>
                        <Tooltip title="Edit expense">
                          <IconButton size="small" aria-label="Edit expense" onClick={() => setEditTarget(expense)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete expense">
                          <IconButton size="small" aria-label="Delete expense" onClick={() => setDeleteTarget(expense)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                  <YourPosition expense={expense} focusEmail={focusEmail} />
                  {expense.totalSplits > 0 && (
                    <Chip
                      label={`${expense.totalSettled}/${expense.totalSplits} settled`}
                      size="small"
                      variant="outlined"
                      color={expense.totalSettled === expense.totalSplits ? 'success' : 'warning'}
                    />
                  )}
                </Stack>
              </Stack>
              <Box mt={1.5}>
                <Typography variant="caption" color="text.secondary">
                  {expense.participantCount} people · {expense.splits
                    .map(s => `${s.isYou ? 'You' : nameOf(s)} ${formatAmount(s.shareAmount)}`)
                    .join(' · ')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <CreateExpenseDialog
        open={!!editTarget}
        expense={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={handleUpdate}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete expense?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.description}" will be removed for everyone in the split and balances
            will be re-calculated. Anything already paid towards it will count as credit.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <LoadingButton onClick={handleDelete} color="error" variant="contained" loading={deleting}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ExpenseList;
